import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SizeType } from '@cf/core';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { PopoverModalComponent } from '../../modals/popover/popover-modal.component';
import { DropdownService } from '../../services/dropdown.service';

@Component({
  selector: 'cf-popover',
  template: `
    <ng-container>
      <div
        (mouseenter)="handlePopover()"
        [ngbPopover]="popContent"
        [popoverTitle]="windowTitle"
        [autoClose]="'outside'"
        popoverClass="pop-{{ popoverSize }} {{ popoverClass }}"
        [triggers]="triggers"
        container="body"
        [class.d-xl-inline-block]="!fullWidth"
        [placement]="placement"
        [class.d-none]="convertToModal"
        [disablePopover]="disablePopover"
      >
        <ng-container *ngTemplateOutlet="popoverToggle"></ng-container>
      </div>

      <div
        *ngIf="convertToModal"
        class="d-xl-none"
        (click)="handleModal(); $event.stopPropagation(); $event.preventDefault()"
      >
        <ng-container *ngTemplateOutlet="popoverToggle"></ng-container>
      </div>

      <ng-template #popContent>
        <ng-container *ngTemplateOutlet="popoverTemplate"></ng-container>
      </ng-template>
    </ng-container>
  `,
  styleUrls: ['./popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgbPopover, NgTemplateOutlet, NgIf],
})
export class PopoverComponent {
  @ViewChild(NgbPopover) popover!: NgbPopover;

  @Input() popoverTemplate!: TemplateRef<any>;

  @Input() popoverToggle!: TemplateRef<any>;

  @Input() windowTitle!: string;

  @Input() popoverSize: SizeType = 'md';

  @Input() placement: PlacementArray = 'auto';

  @Input() triggers = 'mouseenter';

  @Input() popoverClass!: string;

  @Input() disablePopover!: boolean;

  @Input() submitButton!: string;

  @Input() fullWidth = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly dropdownService: DropdownService,
    private readonly modal: NgbModal,
  ) {
    this.dropdownService.hidePopover$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.popover?.close();
    });
  }

  get convertToModal(): boolean {
    return this.triggers !== 'click';
  }

  handlePopover(closeOnClick = false): void {
    if (this.triggers !== 'click' || closeOnClick) {
      this.dropdownService.hidePopover$.next(true);
    }
  }

  handleModal(): void {
    const modal = this.modal.open(PopoverModalComponent);
    (modal.componentInstance as PopoverModalComponent).windowTitle = this.windowTitle;
    (modal.componentInstance as PopoverModalComponent).currentTemplate = this.popoverTemplate;
    (modal.componentInstance as PopoverModalComponent).submitButton = this.submitButton;
  }
}
